import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { random } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { DocumentOverview } from '../../../Document/DocumentOverview';
import {
  useGeInventoryOptions,
  useGePropertyTypes,
  useGePurchaseContractRelationType,
  useGeRealEstateUseTypes,
  useGetRelationshipRoles,
} from '../../../hooks/data';
import { ILegalClient } from '../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipDataPurchaseContractV1 } from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipData';
import {
  createMemberFromLegalClient,
  ILegalTransactionRelationshipMember,
} from '../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { PurchaseContractRealEstatesMain } from '../../PurchaseContract/PurchaseContractRealEstatesMain';
import { PartnerIdentifiers } from './PartnerIdentifiers';
import { RelationshipDataView } from '../../../generic/GenericViewer';
import { RelationshipMembers } from './RelationshipMembers';
import { updateNames } from './utilities';
import { DialogOrigin, DialogOriginPanel } from './DialogOriginPanel';
import {
  RelationshipType,
  RelationshipVersion,
} from '../../../Interfaces/ILegalTransactionRelationship/IRelationshipData/IRelationshipDataViewRegistry';

interface IProps {
  relationship: ILegalTransactionRelationship;
  setRelationship: (newValue: ILegalTransactionRelationship) => void;
  assignedLegalClients: ILegalClient[];
  setAssignedLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
}

const emptyData = {
  withThirdPartyClause: false,
  ThirdPartyClauseText: null,
} as const satisfies ILegalTransactionRelationshipDataPurchaseContractV1;

const groupMembersByRole = (
  members: ILegalTransactionRelationship['Members']
): Record<number, ILegalTransactionRelationshipMember[]> => {
  return members.reduce(
    (acc, member) => {
      const roleId = member.idRelationshipRole;
      if (!acc[roleId]) {
        acc[roleId] = [];
      }
      acc[roleId].push(member);
      return acc;
    },
    {} as Record<number, ILegalTransactionRelationshipMember[]>
  );
};

const PurchaseContractRealEstatesMainLoader = ({
  relationship,
  setRelationship,
  assignedLegalClients,
  setAssignedLegalClients,
}: IProps) => {
  const { propertyTypes, isLoadingPropertyTypes } = useGePropertyTypes();
  const { inventoryOptions, isLoadingInventoryOptions } =
    useGeInventoryOptions();
  const { realEstateUseTypes, isLoadingRealEstateUseTypes } =
    useGeRealEstateUseTypes();
  const {
    purchaseContractRelationTypes,
    isLoadingPurchaseContractRelationTypes,
  } = useGePurchaseContractRelationType();

  if (
    isLoadingPropertyTypes ||
    isLoadingInventoryOptions ||
    isLoadingRealEstateUseTypes ||
    isLoadingPurchaseContractRelationTypes
  ) {
    return <LoadingSpinner height='50vh' />;
  }

  if (
    !propertyTypes ||
    !inventoryOptions ||
    !realEstateUseTypes ||
    !purchaseContractRelationTypes
  ) {
    return <Alert severity='error'>Fehler beim Laden der Daten.</Alert>;
  }

  const updateRelationshipMembers = useCallback(
    (newValue: ILegalClient[], membersRelationshipRoleId: number) =>
      setRelationship({
        ...relationship,
        Members: updateNames(
          [
            ...relationship.Members.filter(
              m => m.idRelationshipRole !== membersRelationshipRoleId
            ),
            ...newValue.map(m => ({
              idRelationship: relationship.idRelationship,
              idRelationshipRole: membersRelationshipRoleId,
              idLegalClient: m.idLegalClient,
              FirstName: m.FirstName || '',
              LastName: m.LastName,
              LegalClient: m,
              LegalTransactionArray: [],
              isActiveLegalClientRelationship: true,
              idLegalClientRelationship:
                relationship.Members.find(
                  x => x.idLegalClient === m.idLegalClient
                )?.idLegalClientRelationship ??
                -random(1, Number.MAX_SAFE_INTEGER),
            })),
          ],
          newValue
        ),
      }),
    [relationship, setRelationship]
  );

  return (
    <PurchaseContractRealEstatesMain
      setMembers={newValue => updateRelationshipMembers(newValue, 1)}
      takenLegalClients={assignedLegalClients}
      setTakenLegalClients={setAssignedLegalClients}
      relationship={relationship}
      setRelationship={setRelationship}
      propertyTypes={propertyTypes}
      inventoryOptions={inventoryOptions}
      realEstateUseTypes={realEstateUseTypes}
      purchaseContractRelationTypes={purchaseContractRelationTypes}
    />
  );
};

export const LegalTransactionRelationshipPresenter: React.FC<IProps> = ({
  relationship,
  setRelationship,
  assignedLegalClients,
  setAssignedLegalClients,
}) => {
  const { relationshipRoles, isLoadingRelationshipRoles } =
    useGetRelationshipRoles();

  const { DocumentArray, Members, data } = relationship;
  console.log(data);

  const identifiers = useMemo(
    () => relationship.PartnerIdentifiers.map(i => i.PartnerIdentifier),
    [relationship.PartnerIdentifiers]
  );

  const updateRelationshipMembers = useCallback(
    (
      newValue: ILegalTransactionRelationshipMember[],
      membersRelationshipRoleId: number
    ) => {
      setRelationship({
        ...relationship,
        Members: [
          ...relationship.Members.filter(
            m => m.idRelationshipRole !== membersRelationshipRoleId
          ),
          ...newValue,
        ],
      });
    },
    [relationship, setRelationship]
  );

  const groupedMembers = useMemo(() => groupMembersByRole(Members), [Members]);

  if (isLoadingRelationshipRoles) {
    return <LoadingSpinner height='50vh' />;
  }

  if (!relationshipRoles) {
    return <Alert severity='error'>Fehler beim Laden der Daten.</Alert>;
  }

  const dialogOrigin = (data as Record<string, unknown> | null)?.dialogOrigin as
    | DialogOrigin
    | undefined;

  return (
    <Accordion>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography variant='h6'>
          {relationship.RelationshipType} - {relationship.idRelationship}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {dialogOrigin && <DialogOriginPanel dialogOrigin={dialogOrigin} />}
        {relationship.RealEstateArray.length > 0 &&
          (relationship.idRelationshipType === 1 ? (
            <PurchaseContractRealEstatesMainLoader
              relationship={relationship}
              setRelationship={setRelationship}
              assignedLegalClients={assignedLegalClients}
              setAssignedLegalClients={setAssignedLegalClients}
            />
          ) : (
            <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Verknüpfte Immobilien
              </Typography>
              <Stack spacing={1}>
                {relationship.RealEstateArray.map((re, idx) => {
                  const r = re as Record<string, unknown>;
                  const street = (r.Street as string) || '';
                  const nr = (r.StreetNr as string) || '';
                  const lr = (r.LandRegister as string) || '';
                  const sheet = (r.SheetLandRegister as string) || '';
                  const area = r.Area as number | undefined;
                  const price = r.Price as number | undefined;
                  return (
                    <Box key={(r.idRealEstate as number) ?? idx}>
                      <Typography variant='body2'>
                        {[street, nr].filter(Boolean).join(' ') || '(ohne Adresse)'}
                        {lr || sheet ? ` · GB ${lr} ${sheet}`.trim() : ''}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {[area && `${area} m²`, price && `${price} €`]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          ))}

        <Typography variant='h6' sx={{ mt: 2 }}>
          Beteiligte
        </Typography>

        {Object.entries(groupedMembers).map(([roleId, members]) => (
          <RelationshipMembers
            relationship={relationship}
            title={
              relationshipRoles?.find(
                role => role.idRelationshipRole === Number(roleId)
              )?.RelationshipRole || 'Unbekannt'
            }
            key={roleId}
            members={members}
            updateMember={member =>
              updateRelationshipMembers(
                members.map(m =>
                  m.idLegalClient === member.idLegalClient ? member : m
                ),
                Number(roleId)
              )
            }
            assignedLegalClients={assignedLegalClients}
            setAssignedLegalClients={(
              newAssignedLegalClients: ILegalClient[]
            ) => {
              setAssignedLegalClients(newAssignedLegalClients);

              const newMemberLegalClients = newAssignedLegalClients.filter(
                lc => !members.some(m => m.idLegalClient === lc.idLegalClient)
              );

              const notDeletedMembers = members.filter(m =>
                newAssignedLegalClients.some(
                  lc => lc.idLegalClient === m.idLegalClient
                )
              );

              const newMembers = newMemberLegalClients.map(legalClient =>
                createMemberFromLegalClient(
                  legalClient,
                  relationship.idRelationship,
                  Number(roleId)
                )
              );

              updateRelationshipMembers(
                [...notDeletedMembers, ...newMembers],
                Number(roleId)
              );
            }}
          />
        ))}
        {data && (
          <RelationshipDataView
            kind={relationship.RelationshipType as RelationshipType}
            version={
              relationship.Version as RelationshipVersion<RelationshipType>
            }
            data={data}
            setData={data => setRelationship({ ...relationship, data })}
          />
        )}
        <DocumentOverview
          allowRemove
          allowUpload
          documentArray={DocumentArray}
          setDocumentArray={newDocuments =>
            setRelationship({
              ...relationship,
              DocumentArray: newDocuments.map(d => ({
                ...d,
                DocumentPurpose: 'Dokument KV',
              })),
            })
          }
          title='Bereitgestellte Daten'
          titleVariant='h6'
          marginTop={5}
        />
        {identifiers.length > 0 && (
          <PartnerIdentifiers identifiers={identifiers} />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
